/**
 * What is coming out of the bank, and how much of it is actually known.
 *
 * THE QUESTION THIS ANSWERS, WHICH NO OTHER SCREEN DOES
 *
 * Every other report on this application looks backwards. The income statement
 * says what happened, the aging screens say what is outstanding, the pay run
 * says what one payroll cost. The operator's question was forward-facing --
 * "what cash outflows are coming up" -- and answering it means putting five
 * sources on one timeline: statutory payroll deposits, bills with terms,
 * invoices with terms, standing commitments the operator recorded, and the pay
 * run that has not been made yet.
 *
 * THE DESIGN RULE, AND IT IS THE WHOLE SCREEN
 *
 * Those sources are not equally true, so they are never added into one number.
 * `lib/cashflow.ts` gives back a band per kind of claim and this screen shows
 * one card per band. A single "you owe $34,000 this quarter" would be the most
 * useful-looking thing here and the most misleading: much of it is a guess
 * about a run nobody has made.
 *
 * WHY THE PROJECTION STOPS AT ONE RUN
 *
 * A quarterly horizon would fit three monthly runs, and projecting all three
 * would look more complete. It would also be wrong in the one place it matters.
 * Each preview is priced by the server against YEAR-TO-DATE wages, and the
 * year-to-date of a projected run does not include the projected runs before
 * it -- so a second projection keeps charging Social Security after the wage
 * base is passed, or misses the additional Medicare threshold. One run ahead is
 * defensible; three would be arithmetic dressed as a forecast. The screen says
 * so rather than quietly stopping.
 *
 * THE HORIZON STARTS ON THE TENANT'S TODAY, NOT THE BROWSER'S
 *
 * `listOpenBills` returns `asOf` -- the business date in the tenant's own
 * timezone, which is what every aging figure on the other screens is measured
 * against. Using the browser's clock instead would put this screen a day out
 * from the Invoices screen for anyone working late, in the direction that hides
 * something due today.
 */

import { offersOwnersEquity, offersPartnersCapital, offersShareholderReport, useAuth, useBusiness } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ApiError,
  api,
  type DepositObligation,
  type InvoiceRow,
  type MissedCommitment,
  type OpenBill,
  type PayrollRunSummary,
  type RecurringCommitment,
  type RunPreview,
} from '../api.js';
import {
  CERTAINTIES,
  buildForecast,
  daysAfter,
  nextOccurrence,
  quarterEnd,
  type CashflowItem,
  type Certainty,
} from '../lib/cashflow.js';
import { UndatedDeposits } from '../components/UndatedDeposits.js';
import { formatCents } from '../lib/money.js';

type Horizon = '30' | '60' | '90' | '180' | 'quarter';

const HORIZONS: Record<Horizon, string> = {
  '30': 'Next 30 Days',
  '60': 'Next 60 Days',
  '90': 'Next 90 Days',
  '180': 'Next 6 Months',
  quarter: 'Rest Of This Quarter',
};

/**
 * Ninety days, and NOT "this quarter", which was the first default here.
 *
 * "This quarter" sounds like the right unit because the tax cadence is
 * quarterly, and it is the wrong one because it SHRINKS. Opened on 16
 * September it is a fourteen-day window, and the largest certain outflow the
 * business has -- the federal deposit for the September pay run, on 15 October
 * -- falls just outside it. The default would then be at its least useful in
 * exactly the last weeks of a quarter, which is when somebody opens a cashflow
 * report. A rolling ninety days is the same length every day of the year.
 *
 * SIX MONTHS IS OFFERED FOR A CONCRETE REASON. The owner-officer is paid
 * QUARTERLY, so the next run is about ninety days out and its deposit about a
 * hundred and twenty. With ninety days as the longest window the projected band
 * would be empty nearly always, and a band that is always empty is one the
 * operator stops reading.
 */
const DEFAULT_HORIZON: Horizon = '90';

/** The last day of the window, from the horizon chosen. */
function horizonEnd(from: string, horizon: Horizon): string {
  return horizon === 'quarter' ? quarterEnd(from) : daysAfter(from, Number(horizon));
}

const BAND_LABELS: Record<Certainty, string> = {
  known: 'Certain',
  due: 'Due On Terms',
  committed: 'Committed',
  projected: 'Projected',
};

const BAND_NOTES: Record<Certainty, string> = {
  known: 'A statutory deadline, computed from the rule. This money is owed.',
  due: 'A bill or invoice with payment terms recorded. The date is the agreed one.',
  committed: 'A standing payment you recorded. Nothing in the books says so yet; the bank sends it.',
  projected: 'The next pay run, if it is made on the date shown. Nothing has happened yet.',
};

/** "October 2026", for a YYYY-MM-DD. */
function monthLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** What the browser thinks today is. Only a fallback; see the header. */
function browserToday(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

interface Loaded {
  /** What the books owe on the payroll tax accounts, planned by the API. */
  deposits: DepositObligation[];
  bills: OpenBill[];
  invoices: InvoiceRow[];
  commitments: RecurringCommitment[];
  missed: MissedCommitment[];
  asOf: string;
  projected: { payDate: string; preview: RunPreview } | null;
  /** Why there is no projection, when there is none. Shown, never swallowed. */
  projectionNote: string | null;
}

/**
 * The next run, priced by the server.
 *
 * It is anchored on the LAST ACTUAL RUN rather than on today: the cadence is a
 * fact about what the business has been doing, and a projection that invented a
 * pay date would be the invented due date migration 0036 refuses, wearing a
 * different hat. No runs means no cadence, and no projection.
 */
async function projectNextRun(
  runs: PayrollRunSummary[],
): Promise<{ projected: Loaded['projected']; note: string | null }> {
  const active = runs.filter((run) => run.status === 'active');
  if (active.length === 0) {
    return { projected: null, note: 'No pay run has been made yet, so there is no cadence to project from.' };
  }

  // The most recent pay date is the anchor; `listPayrollRuns` returns latest
  // first, but sorting here means this does not depend on that.
  const latest = active.reduce((newest, run) => (run.payDate > newest.payDate ? run : newest));

  let frequency;
  try {
    const { employee } = await api.getEmployee(latest.employeeId);
    // The rate in force on the last pay date, which is the one that set the
    // cadence being projected forward.
    const rate = employee.payRates
      .filter((candidate) => candidate.effectiveOn <= latest.payDate)
      .sort((a, b) => b.effectiveOn.localeCompare(a.effectiveOn))[0];
    if (rate === undefined) {
      return { projected: null, note: `${employee.firstName} has no pay rate on or before ${latest.payDate}.` };
    }
    frequency = rate.frequency;
  } catch (caught) {
    return {
      projected: null,
      note: caught instanceof ApiError ? caught.message : 'Could not read the pay rate to project from.',
    };
  }

  const payDate = nextOccurrence(latest.payDate, frequency);
  try {
    const { preview } = await api.previewPayrollRun({ employeeId: latest.employeeId, payDate });
    return { projected: { payDate, preview }, note: null };
  } catch (caught) {
    /*
     * The ordinary reason this fails is a pay date in a year whose tax figures
     * have not been written yet (§18.2), and it is the ordinary case at the end
     * of December. The forecast is still worth showing without the projection,
     * so the refusal becomes a note instead of an error.
     */
    return {
      projected: null,
      note:
        caught instanceof ApiError
          ? `The next run would be ${payDate}, and it cannot be priced: ${caught.message}`
          : `Could not price a run dated ${payDate}.`,
    };
  }
}

export function CashflowPage() {
  const { can } = useAuth();
  const seesPayroll = can('view_payroll');
  const runsPayroll = can('run_payroll');
  // An S corporation's report (0078, 0094); a business taxed with its owner has the owner's equity statement.
  const business = useBusiness();
  const hasShareholders = offersShareholderReport(business);
  const hasOwnersEquity = offersOwnersEquity(business);
  // 0096: a partnership's partners' capital statement.
  const hasPartnersCapital = offersPartnersCapital(business);
  const [searchParams, setSearchParams] = useSearchParams();
  const horizon = (searchParams.get('horizon') as Horizon) in HORIZONS
    ? (searchParams.get('horizon') as Horizon)
    : DEFAULT_HORIZON;
  const includeProjected = searchParams.get('projected') !== 'no';

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    /*
     * One read of everything, once. The sources are read against each other on
     * one timeline, and fetching them as the operator changes the
     * horizon would let a bill paid between two clicks appear on one row of the
     * same screen and not another. The horizon is a filter over what was read,
     * not a new read.
     */
    Promise.all([
      // The runs only anchor the projection; what they owe comes from the plan.
      // Payroll only for someone who may see it (0069); anyone else's forecast
      // has no payroll in it rather than no forecast at all.
      seesPayroll ? api.listPayrollRuns() : Promise.resolve({ runs: [] } as unknown as Awaited<ReturnType<typeof api.listPayrollRuns>>),
      seesPayroll ? api.getDepositTracker() : Promise.resolve({ tracker: { obligations: [] } } as unknown as Awaited<ReturnType<typeof api.getDepositTracker>>),
      api.listOpenBills({ limit: 200 }),
      api.listCommitments(),
      api.listMissedCommitments(),
      /*
       * `listInvoices`, not `listOpenInvoices`. The forecast wants the invoice
       * as a document -- its customer, its terms, what is still owed on it --
       * and `status: 'open'` already means posted with money outstanding,
       * partly paid included. `listOpenInvoices` answers the matching screen's
       * question instead: which A/R LINE can a deposit be allocated against.
       */
      api.listInvoices({ status: 'open', limit: 200 }),
    ])
      .then(async ([runsResult, trackerResult, billsResult, commitmentsResult, missedResult, invoicesResult]) => {
        // Projecting the next pay run asks for its preview, which is running payroll's.
        const { projected, note } = runsPayroll
          ? await projectNextRun(runsResult.runs)
          : { projected: null, note: null };
        setLoaded({
          deposits: trackerResult.tracker.obligations,
          bills: billsResult.bills,
          invoices: invoicesResult.invoices,
          commitments: commitmentsResult.commitments,
          missed: missedResult.missed,
          asOf: billsResult.asOf,
          projected,
          projectionNote: note,
        });
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? caught.message : 'Could not build the forecast.'),
      )
      .finally(() => setLoading(false));
  }, [seesPayroll, runsPayroll]);

  useEffect(load, [load]);

  const from = loaded?.asOf ?? browserToday();
  const to = horizonEnd(from, horizon);

  const forecast = useMemo(
    () =>
      buildForecast({
        from,
        to,
        deposits: loaded?.deposits ?? [],
        bills: loaded?.bills ?? [],
        invoices: loaded?.invoices ?? [],
        commitments: loaded?.commitments ?? [],
        projected: includeProjected ? (loaded?.projected ?? null) : null,
      }),
    [from, to, loaded, includeProjected],
  );

  /** The items grouped by month, with the running net carried through them. */
  const months = useMemo(() => {
    const groups: { month: string; items: { item: CashflowItem; runningCents: number }[] }[] = [];
    let running = 0;
    for (const item of forecast.items) {
      running += item.amountCents;
      const month = item.date.slice(0, 7);
      const last = groups[groups.length - 1];
      if (last !== undefined && last.month === month) last.items.push({ item, runningCents: running });
      else groups.push({ month, items: [{ item, runningCents: running }] });
    }
    return groups;
  }, [forecast]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    setSearchParams(params, { replace: true });
  }

  const netCents = CERTAINTIES.reduce(
    (sum, band) => sum + forecast.inflowCents[band] - forecast.outflowCents[band],
    0,
  );

  /** Where a row's evidence lives. Projected has none yet, so it offers to make it. */
  function evidenceHref(item: CashflowItem): string {
    if (item.source === 'projected-payroll') return '/payroll/runs/new';
    if (item.link?.kind === 'deposit') return '/payroll/deposits';
    if (item.link?.kind === 'commitment') return '/commitments';
    return `/journals/${item.link?.id}`;
  }

  return (
    <section className="reports cashflow">
      {/* The sentence beside the heading names the window, and it prints. */}
      <PrintHeading />
      <header className="row reports-head">
        <h1>Cashflow</h1>
        <span className="muted">
          What leaves and arrives between {from} and {to}, on the dates it is owed. Nothing here is
          paid by this application.
        </span>
        <PrintButton />
      </header>

      <nav className="row tabs" aria-label="Report">
        <Link to="/reports?report=income" className="tab">
          Income Statement
        </Link>
        <Link to="/reports?report=balance" className="tab">
          Balance Sheet
        </Link>
        <Link to="/reports?report=trial" className="tab">
          Trial Balance
        </Link>
        <span className="tab is-current" aria-current="page">
          Cashflow
        </span>
        {hasShareholders && (
          <Link to="/reports/shareholder" className="tab">
            Shareholder
          </Link>
        )}
        {hasOwnersEquity && (
          <Link to="/reports/owners-equity" className="tab">
            Owner&apos;s Equity
          </Link>
        )}
        {hasPartnersCapital && (
          <Link to="/reports/partners-capital" className="tab">
            Partners&apos; Capital
          </Link>
        )}
      </nav>

      <div className="row report-period">
        <label>
          Horizon{' '}
          <select
            aria-label="Horizon"
            value={horizon}
            onChange={(event) => setParam('horizon', event.target.value)}
          >
            {(Object.keys(HORIZONS) as Horizon[]).map((key) => (
              <option key={key} value={key}>
                {HORIZONS[key]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input
            type="checkbox"
            checked={includeProjected}
            onChange={(event) => setParam('projected', event.target.checked ? 'yes' : 'no')}
          />{' '}
          Include Projected Payroll
        </label>
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {loading && <p className="muted">Working…</p>}

      {!loading && !error && loaded !== null && (
        <>
          {/*
            One card per band, never one total. The note under each says what kind of
            claim it is, in the same size as the number, because a reader
            scanning for the biggest figure is exactly the reader who needs to
            know that the biggest figure is a guess.
          */}
          <div className="row cashflow-bands">
            {CERTAINTIES.map((band) => (
              <div key={band} className={`cashflow-band is-${band}`}>
                <h2>{BAND_LABELS[band]}</h2>
                <p className="cashflow-amount">
                  <span className="num">{formatCents(-forecast.outflowCents[band])}</span> out
                </p>
                {forecast.inflowCents[band] > 0 && (
                  <p className="cashflow-amount">
                    <span className="num">{formatCents(forecast.inflowCents[band])}</span> in
                  </p>
                )}
                <p className="muted">{BAND_NOTES[band]}</p>
              </div>
            ))}
          </div>

          {/*
            WHAT DID NOT HAPPEN, above the figures rather than below them.
            A standing instruction that quietly stops leaves no row in the
            ledger for anybody to notice, so this is the one thing on the page
            that cannot be discovered by scrolling. It renders only when there
            is something to say -- unlike the completeness line below it, which
            is always there -- because it names specific dates and a permanent
            "nothing missed" beside a list of dates reads as a list of misses.
          */}
          <UndatedDeposits deposits={forecast.undated} />

          {loaded.missed.length > 0 && (
            <div className="notice notice-warn" role="status">
              <strong>Expected and not seen.</strong>{' '}
              {loaded.missed.length === 1
                ? 'One recorded commitment has no payment against it:'
                : `${loaded.missed.length} recorded commitments have no payment against them:`}
              <ul className="missed-list">
                {loaded.missed.map((row) => (
                  <li key={`${row.commitmentId}-${row.expectedOn}`}>
                    {row.label} — {formatCents(row.expectedCents)} expected {row.expectedOn}
                    {row.seen === null
                      ? row.timing === 'in_month'
                        ? ', nothing found in that month.'
                        : ', nothing found within five days.'
                      : `, but ${formatCents(row.seen.amountCents)} was paid on ${row.seen.entryDate}.`}
                  </li>
                ))}
              </ul>
              <Link to="/commitments">Review Commitments</Link>
            </div>
          )}

          <p className="completeness" role="status">
            <strong>These are not added together.</strong> Certain money is owed under a rule, money
            on terms is owed on a date somebody recorded, and projected money is a run that has not
            been made. Net across all three over this window is {formatCents(netCents)}.
          </p>

          {loaded.projectionNote !== null && includeProjected && (
            <p className="notice notice-warn">{loaded.projectionNote}</p>
          )}

          {/*
            §18.2 and the wage base, stated rather than left as a silent stop.
            An operator who counts three paydays in the quarter and sees one
            projected run should be told why, on the screen, not in a comment.
          */}
          {loaded.projected !== null && includeProjected && (
            <p className="muted">
              One run ahead only. A second projection would be priced against year-to-date wages
              that do not include the first, which goes wrong at the Social Security wage base.
            </p>
          )}

          {forecast.items.length === 0 ? (
            <p className="muted">
              Nothing falls between {from} and {to}. A bill or an invoice appears here once it is
              posted and has payment terms recorded; a payroll deposit appears as soon as the run
              is made.
            </p>
          ) : (
            <div className="table-scroll">
              <table className="lines cashflow-list">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>What</th>
                    <th>Why</th>
                    <th>Certainty</th>
                    <th className="num">Amount</th>
                    <th className="num">Running</th>
                  </tr>
                </thead>
                {months.map((group) => (
                  <tbody key={group.month}>
                    <tr className="report-section">
                      <th colSpan={6}>{monthLabel(`${group.month}-01`)}</th>
                    </tr>
                    {group.items.map(({ item, runningCents }) => (
                      <tr key={`${item.date}-${item.source}-${item.link?.id ?? item.label}`}>
                        <td className="date-cell">{item.date}</td>
                        <td>
                          {/*
                            The drill-down. A figure on a forecast is only
                            trustworthy if the thing behind it is one click
                            away -- the 15 October deposit is the run that
                            created it, and a bill is its journal entry.
                          */}
                          <Link to={evidenceHref(item)}>{item.label}</Link>
                        </td>
                        <td className="muted">{item.detail}</td>
                        <td>
                          <span className={`tag tag-${item.certainty}`}>
                            {BAND_LABELS[item.certainty]}
                          </span>
                        </td>
                        <td className="num">{formatCents(item.amountCents)}</td>
                        <td className="num">{formatCents(runningCents)}</td>
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          )}

          {/*
            Said out loud, because a forecast that omits something reads as
            complete. lib/cashflow.ts carries the same two absences and the
            reasoning; this is the operator-facing half.
          */}
          <p className="muted">
            Not included: money committed at the bank but not yet settled — the Mercury sync skips
            pending transactions on purpose, because the amount is not final — and any standing
            payment nobody has <Link to="/commitments">recorded as a commitment</Link>. Mercury&apos;s
            API has no endpoint for scheduled payments, so a standing order can only be known here
            by being entered.
          </p>
        </>
      )}
    </section>
  );
}
